/** An issue explained: what happened, in words, and what to do about it.
 *
 * The card already prints the job's measurements, and they are the evidence —
 * but "crossing_spread: 38.14 m (11.84 m along the line)" is a fact, not an
 * explanation. This turns one issue's own numbers into two short lists an
 * administrator can act on without knowing how the gate or the survey work:
 * what happened here, and what to do next. Where one resolution is clearly
 * the right first move it is named, so the card can mark that button.
 *
 * Nothing is invented. A sentence that needs a value the evidence does not
 * carry is left out rather than written with a blank in it, and a type this
 * build knows nothing about gets one honest line instead of a guess.
 *
 * Pure, so it is tested without rendering the card.
 */

import type { IssueDetail } from '@/lib/types';

export interface IssueGuide {
  /** What happened, one sentence per point. */
  happened: string[];
  /** What to do, in order. */
  todo: string[];
  /** The resolution id that is the usual right answer, when there is one. */
  recommended: string | null;
}

type GuideInput = Pick<
  IssueDetail,
  'type' | 'official_id' | 'detail' | 'upload' | 'user' | 'status'
> &
  Partial<Pick<IssueDetail, 'owner' | 'pull_request' | 'track_names' | 'gate_rules'>>;

function text(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return '';
}

/** The first number in a measurement the job wrote as text: "38.14 m (…)". */
function leadingNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const match = /-?\d+(?:\.\d+)?/.exec(text(value));
  return match ? Number(match[0]) : null;
}

function prNumber(url: string): string {
  const match = /\/pull\/(\d+)/.exec(url);
  return match ? `#${match[1]}` : 'its pull request';
}

/** A circuit by name when the service knows it, by id otherwise. */
function circuit(input: GuideInput, id: string): string {
  const name = input.track_names?.[id] ?? (id === input.upload?.official_id ? input.upload.track_name : '');
  return name ? `${name} (${id})` : id;
}

export function issueGuide(input: GuideInput): IssueGuide {
  switch (input.type) {
    case 'gate_failed':
      return gateFailed(input);
    case 'layout_mismatch':
      return layoutMismatch(input);
    case 'source_conflict':
      return sourceConflict(input);
    case 'quorum_conflict':
      return {
        happened: [
          'Drivers disagree about what one stretch of border is: a wall, run-off or an ordinary edge. Not enough accounts agree for the job to decide.',
        ],
        todo: [
          'Look at the stretch on the map. If you know what it is, pick that kind and name it in the note (edge, wall, runoff, straddle or auto).',
          'If you are not sure, leave it inferred: the surface data decides, and the drivers’ marks stay as evidence.',
        ],
        recommended: null,
      };
    case 'coverage_regressed':
      return {
        happened: [
          'This survey would draw less of the border than the map shows now, so the job did not merge it.',
        ],
        todo: [
          'Usually the old map is right and the new survey is patchier: reject it and say why in the note.',
          'Accept it only if the lost stretch was wrong before, such as a border drawn somewhere that is not track.',
          'Accepting is remembered: the job merges these uploads on its next run and does not ask about them again. A survey that arrives later is a new question.',
        ],
        recommended: 'reject_upload',
      };
    case 'validation_rejected':
      return {
        happened: [
          'One account sent several files in a day that the service refused as malformed.',
          'An out-of-date or broken logger does this far more often than anyone trying to cause trouble.',
        ],
        todo: [
          'Check the account’s recent uploads for the reason each was refused.',
          'If it looks like a logger problem, contact the person and note what you told them. Disable the account only if it looks deliberate.',
        ],
        recommended: 'contact_user',
      };
    case 'pr_check_failed':
      return {
        happened: ['The job opened a pull request, but the track-data repository’s own checks failed on it.'],
        todo: [
          'Open the pull request on GitHub and read which check failed.',
          'If the check was broken, not the survey, retry. If the survey itself is bad, reject it.',
        ],
        recommended: null,
      };
    case 'upstream_disagrees':
      return {
        happened: [
          'Someone changed this circuit in the track-data repository by hand while the job was merging a survey into it.',
        ],
        todo: [
          'Rebase and retry to merge the survey onto what is there now. Reject it if the hand change should stand alone.',
        ],
        recommended: 'rebase_retry',
      };
    case 'held_expired':
      return {
        happened: ['Uploads that were held for a decision waited too long and were expired.'],
        todo: [
          'Acknowledge this. Any of them can still be re-queued from the Uploads screen.',
        ],
        recommended: 'acknowledge',
      };
    default:
      return {
        happened: ['This build has no description for this kind of issue.'],
        todo: ['Read the measurements below; the summary line says what the job could not decide.'],
        recommended: null,
      };
  }
}

// ── the three that need their own numbers ────────────────────────────────

function gateFailed(input: GuideInput): IssueGuide {
  const detail = input.detail;
  const failed = Array.isArray(detail['failed'])
    ? detail['failed'].filter((item): item is string => typeof item === 'string')
    : [];
  const rules = input.gate_rules;
  const happened: string[] = [];

  const intro = rules?.auto_merge
    ? 'The job opened a pull request for this survey but did not merge it itself, because the gate had doubts.'
    : 'The job opened a pull request for this survey. Auto-merge is off, so it waits for a person either way; the gate also had doubts.';
  happened.push(intro);

  for (const name of failed) {
    if (name === 'crossing_spread') {
      const spread = leadingNumber(detail['crossing_spread']);
      const limit = rules?.crossing_spread_m;
      const crossings = text(detail['finish_crossings']);
      happened.push(
        `Finish line: the ${crossings ? `${crossings} ` : ''}times the lap counter ticked over were recorded ` +
          `${spread !== null ? `${spread.toFixed(0)} m` : 'too far'} apart` +
          `${limit !== undefined ? `, and the gate allows ${limit} m` : ''}. ` +
          'This usually means one tick arrived late, not that the map is wrong, but it leaves the finish line less certain.',
      );
    } else if (name === 'finish_crossings') {
      const crossings = text(detail['finish_crossings']);
      const needed = rules?.min_finish_crossings;
      happened.push(
        `Finish line: only ${crossings || 'too few'} crossing${crossings === '1' ? '' : 's'} recorded` +
          `${needed !== undefined ? `; the gate wants ${needed}` : ''}. Another lap or two would settle it.`,
      );
    } else if (name === 'layout_agreement') {
      const value = text(detail['layout_agreement']);
      if (/signature \(none\)/.test(value)) {
        happened.push(
          `Layout: the contributor said this is ${circuit(input, input.official_id)}, but there is no reference ` +
            'outline for that layout yet, so the gate could not check it. Nothing is known to be wrong; it just could not be confirmed.',
        );
      } else {
        happened.push(
          `Layout: the contributor’s choice and the outline match disagree (${value || 'see below'}).`,
        );
      }
    } else if (name === 'perimeter_complete') {
      happened.push(
        `Borders: not complete all the way round (${text(detail['perimeter_complete']) || 'see below'}).`,
      );
    } else if (name === 'coverage_held') {
      happened.push(
        `Borders: this survey would lose some of what the map already shows (${text(detail['coverage_held']) || 'see below'}).`,
      );
    } else if (name === 'kind_changes') {
      happened.push('Borders: some stretches would change kind (edge, wall, run-off) without enough drivers agreeing.');
    } else {
      happened.push(`The gate also failed “${name}”.`);
    }
  }

  const pr = input.pull_request;
  const todo: string[] = [];
  if (pr && (pr.status === 'auto_merged' || pr.status === 'closed')) {
    todo.push(
      `${prNumber(pr.url)} has already been ${pr.status === 'closed' ? 'closed' : 'merged'} on GitHub, so this is decided. ` +
        'Close the issue with “Keep the PR open”, which records it and changes nothing else.',
    );
    return { happened, todo, recommended: 'keep_open' };
  }
  todo.push('Look at the map below: it is what the pull request would publish.');
  if (failed.includes('layout_agreement')) {
    todo.push('Check it is the layout the contributor says. Compare the corners with the real circuit.');
  }
  todo.push(
    `If it looks right, merge ${pr ? prNumber(pr.url) : 'the pull request'} on GitHub. If it does not, close the pull request and reject the upload here with a note for the contributor.`,
  );
  todo.push('Either way, then close this issue. It does not close by itself.');
  return { happened, todo, recommended: null };
}

function layoutMismatch(input: GuideInput): IssueGuide {
  const detail = input.detail;
  const candidates = Array.isArray(detail['candidates'])
    ? detail['candidates'].filter((item): item is string => typeof item === 'string')
    : [];
  const matched = text(detail['signature_official_id']) || candidates[0] || '';
  const claimed = input.official_id;
  const happened = [
    `The contributor said this survey is ${circuit(input, claimed)}` +
      (matched ? `, but its outline best matches ${circuit(input, matched)}.` : ', but its outline did not match that layout.'),
    'The upload is held until you decide which circuit it belongs to.',
  ];
  const length = leadingNumber(detail['centreline_length_m']);
  if (length !== null) {
    happened.push(
      `The surveyed line is ${Math.round(length).toLocaleString('en-GB')} m long. A survey that is still patchy measures short, and a short line can match the wrong circuit.`,
    );
  }
  return {
    happened,
    todo: [
      'Compare the two lines drawn below with the real circuits.',
      `If it is ${circuit(input, claimed)}, accept the contributor’s layout. If it is ${matched ? circuit(input, matched) : 'the matched layout'}, accept the signature’s.`,
      'If it is neither, or you do not want this survey, reject it with a note.',
    ],
    recommended: null,
  };
}

function sourceConflict(input: GuideInput): IssueGuide {
  const detail = input.detail;
  const source = text(detail['source_id']);
  const owner = input.owner?.display_name || text(detail['owner_user_id']) || 'another account';
  const uploader = input.user?.display_name || text(detail['uploader_user_id']) || 'this account';
  const track = text(detail['track']);
  return {
    happened: [
      `${uploader}’s upload${track ? ` of ${track}` : ''} was refused: everything in it was recorded by installation ${source || '(unknown)'}, which belongs to ${owner}.`,
      'Each logger installation belongs to one account, so nobody can vote twice. Evidence from another account’s installation riding along beside the uploader’s own is set aside without a fuss. This was refused because nothing of the uploader’s own was left.',
      'That happens when a logger re-sends a track file it pulled or imported and never drove itself, when two people share one logger, or when one person has two accounts.',
    ],
    todo: [
      `Transfer only if ${uploader} and ${owner} are the same person, or ${uploader} really owns that logger.`,
      `Otherwise keep the binding. Nothing is lost: ${owner}’s evidence is already theirs, and ${uploader}’s own driving goes through once their logger sends it.`,
    ],
    recommended: 'keep_binding',
  };
}
